<template>
  <div
    :style="{ width }"
    class="snt-autocomplete">
    <v-autocomplete
      :value="value"
      :items="allItems"
      :search-input="searchText"
      :label="label"
      :menu-props="{
        nudgeTop: '-4',
        contentClass:'snt-autocomplete__content'
      }"
      :append-icon="''"
      :hide-details="hideDetails"
      :readonly="readonly"
      :multiple="multiple"
      :item-text="`name`"
      :item-value="itemValue"
      :loading="$apollo.loading"
      hide-selected
      hide-no-data
      outlined
      dense
      chips
      solo
      flat
      v-on="$listeners"
      @update:search-input="setSearchDebounce($event)">
      <template #selection="{ attrs, selected, select, item, index }">
        <v-chip
          v-bind="attrs"
          :input-value="selected"
          class="suggest-chip ma-1"
          color="primary"
          outlined
          label
          small
          close
          @click="select"
          @click:close="remove(index)">
          {{ item.name }}
        </v-chip>
      </template>
      <template #item="{ item }">
        <v-row
          class="text-description"
          dense>
          <v-col cols="3">
            <div class="word-break-all">
              {{ item.code }}
            </div>
          </v-col>
          <v-col cols="7">
            <div v-html="highlightText(item.name)"></div>
          </v-col>
          <v-col cols="2">
            <div class="word-break-all">
              {{ item.price | toLocaleStringDecimal }}
            </div>
          </v-col>
        </v-row>
      </template>
      <template #append-item>
        <infinite-loading
          force-use-infinite-wrapper=".v-select-list"
          @infinite="infiniteHandlerDebounce($event)">
          <div slot="no-more" />
          <div slot="no-results" />
        </infinite-loading>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import gql from 'graphql-tag'

export default {
  props: {
    value: {
      type: [String, Array],
      default: () => ([])
    },
    label: {
      type: String,
      default: undefined
    },
    itemValue: {
      type: String,
      default: 'id'
    },
    width: {
      type: [String, Number],
      default: '100%'
    },
    readonly: Boolean,
    hideDetails: Boolean,
    multiple: Boolean
  },
  apollo: {
    productList: {
      fetchPolicy: 'network-only',
      query: gql`
        query (
          $page: Int
          $perPage: Int
          $search: String
        ) {
          productList(
            page: $page
            perPage: $perPage
            search: $search
          ) {
            hasNextPage
            nodes {
              id
              code
              name
              price
            }
          }
        }
      `,
      variables () {
        return {
          page: 1,
          perPage: this.perPage,
          search: this.searchText
        }
      },
      result ({ data }) {
        this.onLoaded(data)
      }
    }
  },
  data () {
    return {
      searchText: '',
      page: 1,
      perPage: 15,
      search: '',
      productList: {
        nodes: [],
        hasNextPage: false
      }
    }
  },
  computed: {
    items () {
      return this.productList.nodes
    },
    allItems () {
      if (this.multiple) {
        const newItems = this.value.map((tag) => ({ [this.itemValue]: tag }))
        const allItems = this.$_.concat(this.items, newItems)
        return this.$_.uniqBy(allItems, this.itemValue)
      }
      return this.items
    }
  },
  created () {
    this.infiniteHandlerDebounce = this.$_.debounce(this.infiniteHandler, 100)
    this.setSearchDebounce = this.$_.debounce(this.setSearch, 800)
  },
  methods: {
    infiniteHandler ($state) {
      if (this.productList.hasNextPage) {
        this.page += 1
        this.$apollo.queries.productList.fetchMore({
          variables: {
            page: this.page,
            perPage: this.perPage
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const { hasNextPage, nodes } = fetchMoreResult.productList
            return ({
              productList: {
                __typename: previousResult.productList.__typename,
                nodes: this.$_.uniqBy([
                  ...previousResult.productList.nodes,
                  ...nodes
                ], this.itemValue),
                hasNextPage
              }
            })
          }
        }).then(() => {
          $state.loaded()
        })
      } else {
        $state.complete()
      }
    },
    highlightText (str) {
      if (!this.searchText) return str
      return this.$_.replace(
        str,
        this.searchText,
        `<span class="primary--text">${this.searchText}</span>`
      )
    },
    setSearch (searchText = '') {
      this.page = 1
      this.searchText = searchText
    },
    remove (index) {
      if (this.multiple) {
        const values = this.$_.cloneDeep(this.value)
        values.splice(index, 1)
        this.$emit('input', values)
      } else {
        this.$emit('input', null)
      }
    },
    onLoaded (data) {
      this.$emit('loaded', data)
    }
  }

}
</script>

<style lang="scss" scoped>
  .snt-autocomplete {
    &__content {
      .v-select-list {
        padding: 8px;
        max-width: fit-content;
        min-width: 100%;
        box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);
        border-radius: 4px;
        ::v-deep {
          .v-list-item {
            border-bottom: 1px solid #ddd;
            padding: 4px;
          }
        }
      }
    }
  }
  .suggest-chip.v-chip.v-chip--outlined.v-chip.v-chip {
    cursor: pointer;
    white-space: pre-line;
    background-color: var(--v-select-base) !important;
    height: 100%;
  }
</style>
